// ProjectSwitcher.tsx (uppdaterad)
import { useMemo } from "react";
import { useLocation, useNavigate, useMatch } from "react-router-dom";
import { useGetMultiplacerProjectsQuery } from "../openapi";
import { McCombobox } from "./components/McCombobox/McCombobox";
import { isProjectViewerOnlyProject } from "./projectViewerOnlyProject";

type Project = { projectNo: string | number; title: string };

export function ProjectSwitcher({
  width = 320,
  defaultTail = "design",
}: {
  width?: number | string;
  defaultTail?: string;
}) {
  const navigate = useNavigate();
  const location = useLocation();

  // ✅ Fungerar även i headern utanför barn-routes
  const match = useMatch("/:projectId/*");
  const projectId = match?.params.projectId ?? null;

  const { data: projects = [], isFetching } = useGetMultiplacerProjectsQuery();

  const options = useMemo(
    () =>
      projects.map((p: Project) => ({
        ...p,
        label: `${p.title} (${p.projectNo})`,
      })),
    [projects]
  );

  const currentValue = useMemo(
    () => options.find((o) => String(o.projectNo) === String(projectId)) ?? null,
    [options, projectId]
  );

  const handleSelect = (p: (Project & { label: string }) | null) => {
    if (!p) return;
    const parts = location.pathname.split("/").filter(Boolean);
    const currentTail = parts.slice(1).join("/") || defaultTail;
    const tail = isProjectViewerOnlyProject(p.projectNo) ? "viewer" : currentTail;
    const search = location.search || "";
    const hash = location.hash || "";
    navigate(`/${p.projectNo}/${tail}${search}${hash}`);
  };

  const placeholder = currentValue
    ? undefined
    : isFetching
      ? "Laster prosjekter…"
      : "Velg prosjekt";

  return (
    <div style={{ width }} title={currentValue?.label ?? "Velg prosjekt"}>
      <McCombobox
        options={options}
        currentValue={currentValue}
        onChange={handleSelect}
        displayProperty="label"
        placeholder={placeholder}
      />
    </div>
  );
}
